import { Control, Controller, RegisterOptions } from "react-hook-form";
import { useCallback, useMemo } from "react";

import { NoxusInput } from "@/components/Form/InputField";
import { Plus, Trash } from "lucide-react";
import { RichTextValue } from "./RichTextField";
import TiptapEditor from "./TiptapEditor/TiptapEditor";
import { SpotSelect } from "./SelectField";
import { type AdvancedChipOption } from "./TiptapEditor/chipOptionsConfig";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { NoxusNumberInput } from "./NumberInputField";
import { Switch } from "../shadcn/switch";
import { Button } from "../shadcn/button";

type RegularCondition =
  | "CONTAINS"
  | "DOES_NOT_CONTAIN"
  | "STARTS_WITH"
  | "EQUALS"
  | "NOT_EQUALS"
  | "GREATER_THAN"
  | "GREATER_THAN_OR_EQUAL"
  | "SMALLER_THAN"
  | "SMALLER_THAN_OR_EQUAL";

export interface IConditionalValue {
  logic?: "AND" | "OR";
  condition_type: "REGULAR" | "AI";
  regular_condition?: {
    field: string | { label: string; value: string };
    field_type: "TEXT" | "NUMBER";
    condition: RegularCondition;
    text_value?: string;
    number_value?: number;
  };
  ai_condition?: {
    prompt: RichTextValue;
  };
}

interface IComplexConditionalInput {
  title?: string;
  value: IConditionalValue[];
  tooltip?: string;
  onChange: (value: IConditionalValue[]) => void;
  input_options: AdvancedChipOption[];
  name?: string;
  isDisabled?: boolean;
}

const ComplexConditionalInput = (props: IComplexConditionalInput) => {
  const { value, input_options } = props;
  const isDisabled = props.isDisabled;

  const cantDelete = useMemo(
    () => value.length <= 1 || !!isDisabled,
    [value, isDisabled]
  );

  const handleAddClick = useCallback(() => {
    if (isDisabled) return;
    const newDictArray = [
      ...props.value,
      {
        logic: "AND" as "AND" | "OR",
        condition_type: "REGULAR" as "REGULAR" | "AI",
        regular_condition: {
          field: input_options[0].values[0],
          field_type: "TEXT" as "TEXT" | "NUMBER",
          condition: "EQUALS" as RegularCondition,
          text_value: "",
          number_value: 0,
        },
      },
    ];
    props.onChange(newDictArray);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props, isDisabled]);

  const handleDeleteClick = useCallback(
    (indexToRemove: number) => {
      if (cantDelete) return;

      const newDictArray = [
        ...props.value
          .slice(0, indexToRemove)
          .concat(props.value.slice(indexToRemove + 1)),
      ];
      props.onChange(newDictArray);
    },
    [cantDelete, props]
  );

  const handleChange = useCallback(
    (value: IConditionalValue, index: number) => {
      if (isDisabled) return;
      props.onChange(props.value.map((d, i) => (i === index ? value : d)));
    },
    [props, isDisabled]
  );

  return (
    <>
      {props.title && (
        <NoxusFormLabel
          label={props.title}
          tooltip={props.tooltip}
          name={props.name}
        />
      )}
      <div
        className="flex flex-col items-start gap-4"
        style={{ marginTop: props.title ? "0.5rem" : 0 }}
      >
        {props.value.map((dict, index: number) => (
          <Entry
            key={`dict-${index}`}
            value={dict}
            onChange={(value) => handleChange(value, index)}
            handleDeleteClick={() => handleDeleteClick(index)}
            index={index}
            input_options={input_options}
            isDisabled={cantDelete}
          />
        ))}
        <Button
          className="mt-1 w-full"
          variant="outline"
          onClick={handleAddClick}
          isDisabled={isDisabled}
        >
          <Plus size="1.25rem" strokeWidth="1.25px" />
          {props.value.length > 0 ? "Add more" : "Add first condition"}
        </Button>
      </div>
    </>
  );
};

const Entry = ({
  value,
  onChange,
  handleDeleteClick,
  index,
  input_options,
  isDisabled,
}: {
  value: IConditionalValue;
  onChange: (value: IConditionalValue) => void;
  handleDeleteClick: () => void;
  index: number;
  input_options: AdvancedChipOption[];
  isDisabled: boolean;
}) => {
  return (
    <div className="w-full p-4 border border-border rounded-xl relative">
      <div className="flex justify-between items-start w-full mb-4">
        <div className="h-fit">
          <h2 className="text-sm font-normal leading-none mb-1">
            AI condition
          </h2>
          <span className="text-xs font-normal text-muted-foreground">
            Give a condition to AI
          </span>
        </div>
        <Switch
          checked={value.condition_type === "AI"}
          onCheckedChange={(checked) => {
            onChange({
              ...value,
              condition_type: checked ? "AI" : "REGULAR",
            });
          }}
        />
      </div>
      {value.condition_type === "AI" && (
        <TiptapEditor
          chipOptions={input_options}
          label="Prompt"
          value={value.ai_condition?.prompt}
          onChange={(richTextValue) => {
            onChange({
              ...value,
              ai_condition: { prompt: richTextValue },
            });
          }}
          isControlled
        />
      )}
      {value.condition_type === "REGULAR" && (
        <div className="flex flex-col gap-4">
          <SpotSelect
            options={[
              { label: "Text", value: "TEXT" },
              { label: "Number", value: "NUMBER" },
            ]}
            onChange={(selectedValue) => {
              if (value.regular_condition) {
                const newFieldType = selectedValue.value as "TEXT" | "NUMBER";
                onChange({
                  ...value,
                  regular_condition: {
                    ...value.regular_condition,
                    field_type: newFieldType,
                    // Initialize values when switching types
                    text_value:
                      newFieldType === "TEXT"
                        ? value.regular_condition.text_value || ""
                        : "",
                    number_value:
                      newFieldType === "NUMBER"
                        ? value.regular_condition.number_value || 0
                        : 0,
                    // Reset condition to a default when switching types
                    condition: newFieldType === "TEXT" ? "CONTAINS" : "EQUALS",
                  },
                });
              }
            }}
            value={value.regular_condition?.field_type}
            label="Condition type"
          />
          <div className="flex items-end gap-4">
            <SpotSelect
              options={(input_options[0]?.values ?? []).map((value) => ({
                label: typeof value === "string" ? value : value.label,
                value: typeof value === "string" ? value : value.value,
              }))}
              onChange={(selectedValue) => {
                if (value.regular_condition) {
                  onChange({
                    ...value,
                    regular_condition: {
                      ...value.regular_condition,
                      field: selectedValue as string,
                    },
                  });
                }
              }}
              value={value.regular_condition?.field}
              label="Field"
            />

            <SpotSelect
              options={
                value.regular_condition?.field_type === "NUMBER"
                  ? [
                      { label: "Equals", value: "EQUALS" },
                      { label: "Not equals", value: "NOT_EQUALS" },
                      { label: "Greater than", value: "GREATER_THAN" },
                      {
                        label: "Greater than or equal to",
                        value: "GREATER_THAN_OR_EQUAL",
                      },
                      { label: "Smaller than", value: "SMALLER_THAN" },
                      {
                        label: "Smaller than or equal to",
                        value: "SMALLER_THAN_OR_EQUAL",
                      },
                    ]
                  : value.regular_condition?.field_type === "TEXT"
                    ? [
                        { label: "Contains", value: "CONTAINS" },
                        {
                          label: "Does not contain",
                          value: "DOES_NOT_CONTAIN",
                        },
                        { label: "Starts with", value: "STARTS_WITH" },
                      ]
                    : []
              }
              onChange={(selectedValue) => {
                if (value.regular_condition) {
                  onChange({
                    ...value,
                    regular_condition: {
                      ...value.regular_condition,
                      condition: selectedValue as RegularCondition,
                    },
                  });
                }
              }}
              value={value.regular_condition?.condition}
            />
          </div>
          {value.regular_condition?.field_type === "TEXT" && (
            <NoxusInput
              value={value.regular_condition?.text_value ?? ""}
              onChange={(e) => {
                if (value.regular_condition) {
                  onChange({
                    ...value,
                    regular_condition: {
                      ...value.regular_condition,
                      text_value: e.target.value,
                    },
                  });
                }
              }}
              onBlur={() => {
                if (
                  value.regular_condition &&
                  value.regular_condition.text_value !==
                    value.regular_condition.text_value?.trim()
                ) {
                  onChange({
                    ...value,
                    regular_condition: {
                      ...value.regular_condition,
                      text_value: (
                        value.regular_condition.text_value ?? ""
                      ).trim(),
                    },
                  });
                }
              }}
              placeholder="Value"
            />
          )}
          {value.regular_condition?.field_type === "NUMBER" && (
            <NoxusNumberInput
              value={value.regular_condition?.number_value ?? 0}
              onChange={(e) => {
                if (value.regular_condition) {
                  onChange({
                    ...value,
                    regular_condition: {
                      ...value.regular_condition,
                      number_value: e,
                    },
                  });
                }
              }}
              min={Number.MIN_SAFE_INTEGER}
            />
          )}
        </div>
      )}
      <div className="absolute -top-4 -right-4 bg-background rounded-full">
        <Button
          onClick={handleDeleteClick}
          variant="outline"
          className="rounded-full h-8 w-8 p-0"
          isDisabled={isDisabled}
        >
          <Trash strokeWidth={1.25} size={16} />
        </Button>
      </div>
      {index !== 0 && (
        <div className="absolute -top-7 left-8 bg-background">
          <SpotSelect
            options={[
              { label: "AND", value: "AND" },
              { label: "OR", value: "OR" },
            ]}
            value={value.logic}
            onChange={(selectedValue) => {
              onChange({
                ...value,
                logic: selectedValue as "AND" | "OR",
              });
            }}
            isNotSearchable
          />
        </div>
      )}
    </div>
  );
};

interface IComplexConditionalField {
  name: string;
  tooltip?: string;
  label?: string;
  rules?: RegisterOptions;
  control: Control<any>;
  defaultValue?: Record<string, string>[];
  input_options: AdvancedChipOption[];
  isDisabled?: boolean;
}

export const ComplexConditionalField = (props: IComplexConditionalField) => {
  return (
    <Controller
      control={props.control}
      name={props.name}
      rules={props.rules}
      defaultValue={props.defaultValue}
      render={({ field: { onChange, value } }) => {
        return (
          <ComplexConditionalInput
            value={value || []}
            title={props.label} // this value will be used on the Add more button
            tooltip={props.tooltip}
            onChange={onChange}
            input_options={props.input_options}
            name={props.name}
            isDisabled={props.isDisabled}
          />
        );
      }}
    />
  );
};
